import { useTranslation } from 'react-i18next';
import { LockFailureReasons } from '@/accessControl/LockFailureReasons.atom';
import { sqWorkbookStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { ExternalLink, Icon } from '@seeqdev/qomponents';
import type { LockErrorOutputV1 } from '@seeqdev/api/model/LockErrorOutputV1';

/**
  Display a list of global items that are preventing a user locking an Analysis or Vantage Room. Underneath each item,
  display a reason why the lock failed and a link to where the item is used.
 */

interface GlobalItemsProps {
  hasLockingFailedPreviouslyDueToGlobalItems: boolean;
}

export const GlobalItems = ({ hasLockingFailedPreviouslyDueToGlobalItems }: GlobalItemsProps) => {
  const { t } = useTranslation();
  const { lockingErrors, isVantage } = useFlux(sqWorkbookStore);

  const cannotLockKey = isVantage ? 'ACCESS_CONTROL.LOCKING.CANNOT_LOCK_VANTAGE' : 'ACCESS_CONTROL.LOCKING.CANNOT_LOCK';
  const globalItemsTooltipKey = isVantage
    ? 'ACCESS_CONTROL.LOCKING.GLOBAL_ITEMS_TOOLTIP_VANTAGE'
    : 'ACCESS_CONTROL.LOCKING.GLOBAL_ITEMS_TOOLTIP';

  const HelpIcon = (
    <ExternalLink href={`${SUPPORT_LINK}/locking-a-workbench-analysis`} variant="icon">
      <Icon
        icon="fa-question-circle"
        large={true}
        tooltip={t(globalItemsTooltipKey)}
        extraClassNames="text-interactive"
      />
    </ExternalLink>
  );

  return (
    <>
      {lockingErrors && lockingErrors.length > 0 && (
        <>
          <div className="tw:pb-sq-10" data-testid="cannotLockTitle">
            {hasLockingFailedPreviouslyDueToGlobalItems && (
              <>
                <strong>{t(cannotLockKey)}</strong> {HelpIcon}
              </>
            )}
            {!hasLockingFailedPreviouslyDueToGlobalItems && (
              <>
                <strong>{t('ACCESS_CONTROL.LOCKING.PREVIOUSLY_ATTEMPT_LOCKED')}</strong> {HelpIcon}
                <div>
                  <strong>{t('ACCESS_CONTROL.LOCKING.REATTEMPT_LOCKING')}</strong>
                </div>
              </>
            )}
          </div>
          <div className="tw:p-sq-10" style={{ backgroundColor: 'var(--sq-background-secondary)' }}>
            {lockingErrors.length > 0 &&
              lockingErrors.map((globalItem: LockErrorOutputV1) => (
                <div key={`error-container-${globalItem.item.id}`} className="tw:mb-5">
                  <LockFailureReasons itemName={globalItem.item.name} globalItem={globalItem} isVantage={isVantage} />
                </div>
              ))}
          </div>
        </>
      )}
    </>
  );
};
